/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action, computed } from "mobx";
import { subscribeToSelector } from "dc-preview-utils/src/Utils/MobXUtils";
import * as constants from "constants/constants";
import { pageViewModifiers, docViewModes, ZOOM_TRIGGER_TYPE } from "constants/enums";
import * as ZoomUtils from "utils/ZoomUtils";
import { getCurrentPageIndex, getPageBeforeOrAtOffset, getCurrentViewState } from "selectors/pageNumber";
import { pagesInView } from "selectors/pagedViewSelectors";
import { getPagesViewXPos } from "selectors/pagedViewState";
import { isUndefined } from "utils/lodashUtils";
import { getRowIndex, getRowOrigHeight, getRowOrigWidth } from "selectors/pageSelectors";
import {
  getTotalPages,
  getMaxPageWidth,
  getMaxPageHeight,
  isSinglePageMode,
  isClassicPDF,
  isTwoColumnViewMode,
  isFitOneFullPageMode,
  isFullScreenOrFitOnePageMode,
  isFullScreenMode
} from "selectors/documentSelectors";
import {
  getViewPortHeightWithoutPagesViewScrollbar,
  getViewPortWidthWithoutPagesViewScrollbar,
  getViewPortHeight
} from "selectors/documentViewSelectors";
import {
  getCurrentPageZoomLevel,
  getPinchZoomScrollLeft,
  getPinchZoomScrollTop,
  getPinchZoomInitialZoom,
  getPageViewModifier,
  getMinZoomLevelForMobile,
  getProvidedMargin
} from "../selectors/zoom";
import { getShouldUseModernViewer, getShouldUseMobileModernViewer } from "selectors/appSelectors";
import { overlayEvents } from "overlays/OverlayEvents";
import { trackZoomIn, trackZoomOut, trackZoomReset, trackOnViewModifierUpdate } from "analytics/events";
import { PinchZoomStore } from "./PinchZoomStore";
import { isCurrentDeviceMobile, getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { roundOffTo2Decimal } from "utils/MathUtils";
import { getPageHandle } from "selectors/pageSelectors";
import { triggerPagesInViewEvent } from "publicApis/Events/events";
import { isPersonalCommentingEnabled } from "../utils/FeatureFlagUtils";
import { CLEAR_QUEUE_EVENTS } from "api/events/clearAJSQueueEvents";
import { contextMenuManager } from "../contextMenuManager";
import { PAGES_VIEW_MARGIN_LEFT, PAGES_VIEW_MARGIN_RIGHT, PAGE_BOTTOM_MARGIN } from "../constants/constants";
import { getViewMode } from "selectors/viewModeSelectors";

class Zoom {
  @observable minZoom;
  @observable maxZoom;
  @observable fixedMargins;
  @observable pageViewModifier;
  @observable pageZoomLevel;
  @observable currentDVZoomLevel;
  @observable pinchZoomStore;
  @observable isCommentsPanelVisible;
  zoomLevelPageIndex;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
      this.setFixedMargins({
        left: "auto",
        right: "auto"
      });
    }
  }

  @action
  init() {
    this.minZoom = constants.DEFAULT_MIN_ZOOM * getDevicePixelRatio();
    this.maxZoom = constants.DEFAULT_MAX_ZOOM * getDevicePixelRatio();
    this.setFixedMargins({
      left: "auto",
      right: "auto"
    });
    this.pageViewModifier = pageViewModifiers.NO_MOD;
    this.pageZoomLevel = constants.DEFAULT_ZOOM_LEVEL;
    this.currentDVZoomLevel = 100;
    this.pinchZoomStore = new PinchZoomStore();
    this.isCommentsPanelVisible = false;

    // non-observables
    this.providedFixedWidthMargin = undefined;
    this.allowInitialZoomlevelChange = true;
    this.isSubscribedToTwoPageViewSelector = false;
  }

  @action
  setFixedMargins(margins) {
    const shouldUseMobileModernViewer = this.stores && getShouldUseMobileModernViewer(this.stores);
    this.fixedMargins = margins;
    if (!this.isSubscribedToTwoPageViewSelector) {
      if (this.stores && getShouldUseModernViewer(this.stores) && !shouldUseMobileModernViewer) {
        this.isSubscribedToTwoPageViewSelector = true;
        subscribeToSelector(this.stores, isTwoColumnViewMode, isTwoColumnMode => {
          if (isTwoColumnMode) {
            this.setFixedMargins({
              left: "auto",
              right: "auto"
            });
          }
        });
      }
    }
    if (
      this.stores &&
      getShouldUseModernViewer(this.stores) &&
      !shouldUseMobileModernViewer &&
      !isTwoColumnViewMode(this.stores) &&
      getViewMode(this.stores) !== docViewModes.IN_PLACE &&
      getViewMode(this.stores) !== docViewModes.SINGLE_PAGE &&
      !isFullScreenOrFitOnePageMode(this.stores)
    ) {
      this.fixedMargins = {
        left: PAGES_VIEW_MARGIN_LEFT,
        right: PAGES_VIEW_MARGIN_RIGHT
      };
    }
  }

  /* minZoom and maxZoom represent device Zoom Levels which is
    zoomLevels * devicepixelratio.*/
  @action
  setZoomLimits({ minZoom, maxZoom }) {
    if (!isUndefined(minZoom)) {
      this.minZoom = minZoom;
    }
    if (!isUndefined(maxZoom)) {
      this.maxZoom = maxZoom;
    }
  }

  @action
  setNewZoomLevelObservables(newZoomLevel, newPageViewModifier, pageIndex) {
    let minZoom = this.minZoom / getDevicePixelRatio();
    let maxZoom = this.maxZoom / getDevicePixelRatio();
    newZoomLevel = ZoomUtils.getTruncatedZoomLevel(minZoom, maxZoom, newZoomLevel);

    if (this.pageZoomLevel !== newZoomLevel) {
      this.pageZoomLevel = newZoomLevel;
    }
    if (this.pageViewModifier !== newPageViewModifier) {
      this.pageViewModifier = newPageViewModifier;
    }
  }

  @action
  setIsCommentsPanelVisible(visibility) {
    const lastViewState = getCurrentViewState(this.stores);
    const shouldUseMobileModernViewer = getShouldUseMobileModernViewer(this.stores);
    // don't set the value here; first set the mode and perform custom zoom according to current conditions
    if (visibility) {
      const providedMargin = getProvidedMargin(this.stores);
      if (providedMargin) this.setFixedWidthMargin(providedMargin.left, providedMargin.right);
      if (this.pageViewModifier === pageViewModifiers.FIT_WIDTH) {
        if (getShouldUseModernViewer(this.stores) && !shouldUseMobileModernViewer) {
          this.setPageViewModifier(
            {
              newPageViewModifier: pageViewModifiers.FIT_WIDTH
            },
            lastViewState
          );
        } else {
          this.setPageViewModifier(
            {
              newPageViewModifier: pageViewModifiers.FIXED_WIDTH,
              fixedMargins: { left: 0, right: 0 }
            },
            lastViewState
          );
        }
      } else if (this.pageViewModifier === pageViewModifiers.FIXED_WIDTH) {
        this.setPageViewModifier(
          {
            newPageViewModifier: pageViewModifiers.FIXED_WIDTH
          },
          lastViewState
        );
      }
    } else {
      if (
        isPersonalCommentingEnabled(this.stores) ||
        (getShouldUseModernViewer(this.stores) && !shouldUseMobileModernViewer)
      ) {
        /* If fit width mode was ON and comments panel was visible then on closing comments panel we need to remove fixedWidthMode but this will reset the pageViewModifier to NO_MOD hence we need to preserve the fact that current mode was fit width.  */
        const wasFitWidthModeOn =
          this.pageViewModifier === pageViewModifiers.FIXED_WIDTH && this.isCommentsPanelVisible;
        this.removeFixedWidthMode(false);
        if (wasFitWidthModeOn) {
          this.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_WIDTH }, lastViewState);
        }
      } else {
        this.setFixedMargins({
          left: "auto",
          right: "auto"
        });
        this.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIXED_WIDTH }, lastViewState);
      }
    }

    // now set the actual value
    this.isCommentsPanelVisible = visibility;
  }

  @action
  setPageZoomLevel(initialZoomLevel = constants.DEFAULT_ZOOM_LEVEL) {
    this.pageZoomLevel = initialZoomLevel;
  }

  @action
  performZoomAction(zoomParameters, triggerType) {
    let { pageIndex, pageBottomMargin, yInPage } = getPageBeforeOrAtOffset(this.stores);
    if (yInPage !== -1) {
      yInPage /= this.getCurrPageZoomLevel(pageIndex);
    }
    let xInPage = getPagesViewXPos(this.stores) / this.getCurrPageZoomLevel(pageIndex);
    this.performCustomZoom({ pageIndex, xInPage, yInPage, pageBottomMargin }, zoomParameters, triggerType);
  }

  _reloadPages = pagesInViewObj => {
    this.stores.documentStore.clearLoadPageRequests({
      evtName: CLEAR_QUEUE_EVENTS.ZOOM_LEVEL_CHANGE,
      zoomLevel: this.pageZoomLevel
    });
    this.stores.documentStore.setPagesCacheWindow(this.pageZoomLevel);
    this.stores.documentStore.reloadPages(pagesInViewObj);
  };

  @action
  performCustomZoom(
    { pageIndex, xInPage, yInPage, pageBottomMargin },
    { newZoomLevel, newPageViewModifier = pageViewModifiers.NO_MOD },
    triggerType = ZOOM_TRIGGER_TYPE.OTHER
  ) {
    let haveZoomlevelsChanged = false;

    let initialPageZoomLevel = this.pageZoomLevel;
    this.setNewZoomLevelObservables(newZoomLevel, newPageViewModifier, pageIndex);

    if (initialPageZoomLevel !== this.pageZoomLevel) {
      haveZoomlevelsChanged = true;
    }
    this.stores.pagedViewState.goToLocation(pageIndex, xInPage, yInPage, pageBottomMargin);
    let pagesInViewObj = pagesInView(this.stores);
    if (haveZoomlevelsChanged) {
      if (isClassicPDF(this.stores)) {
        this._reloadPages(pagesInViewObj);
      }
      this.stores.documentStore.setShouldRecomputePageSizes(haveZoomlevelsChanged);
      overlayEvents.trigger("zoom", getCurrentPageZoomLevel(this.stores), triggerType);
      triggerPagesInViewEvent(this.stores);
      this.stores.documentStore.getPageRenditionInfo &&
        this.stores.documentStore.getPageRenditionInfo.numZoomOperations++;
    }
    contextMenuManager.clearMenuItems(this.stores);
  }

  @action
  zoomIn(triggerType) {
    const newZoomLevel = ZoomUtils.getZoomInLevel(getCurrentPageZoomLevel(this.stores));
    this.performZoomAction({ newZoomLevel }, triggerType);
    trackZoomIn();
  }

  @action
  zoomOut(triggerType) {
    const newZoomLevel = ZoomUtils.getZoomOutLevel(getCurrentPageZoomLevel(this.stores));
    this.performZoomAction({ newZoomLevel }, triggerType);
    trackZoomOut();
  }

  @action
  resetZoom(triggerType) {
    const newZoomLevel = constants.DEFAULT_ZOOM_LEVEL;
    this.performZoomAction({ newZoomLevel }, triggerType);
    trackZoomReset();
  }

  @action
  setPageViewModifier({ newPageViewModifier = pageViewModifiers.NO_MOD }, lastViewState = undefined) {
    trackOnViewModifierUpdate(this.pageViewModifier, newPageViewModifier);
    // Switch to CLASSIC_VIEW if currently it is slide mode and newPageViewModifier is different from NO_MOD
    // or Switch to CLASSIC_VIEW if currently it is fit one mode and newPageViewModifier is FIT_WIDTH or FIXED_WIDTH
    if (
      (isSinglePageMode(this.stores) && newPageViewModifier !== pageViewModifiers.NO_MOD) ||
      (isFitOneFullPageMode(this.stores) &&
        (newPageViewModifier === pageViewModifiers.FIT_WIDTH || newPageViewModifier === pageViewModifiers.FIXED_WIDTH))
    ) {
      this.stores.documentStore.setViewMode(docViewModes.CLASSIC_VIEW);
    }
    if (newPageViewModifier !== pageViewModifiers.FIT_PAGE) {
      this.stores.documentStore.toggleViewWithScrolling(true);
    }

    let pageIndex = lastViewState ? lastViewState.pageIndex : getCurrentPageIndex(this.stores);
    switch (newPageViewModifier) {
      case pageViewModifiers.FIT_WIDTH:
        if (lastViewState) {
          this.performCustomZoom(lastViewState, {
            newZoomLevel: this.getFitWidthZoomLevel(),
            newPageViewModifier: pageViewModifiers.FIT_WIDTH
          });
        } else {
          this.performZoomAction({
            newZoomLevel: this.getFitWidthZoomLevel(),
            newPageViewModifier: pageViewModifiers.FIT_WIDTH
          });
        }
        break;
      case pageViewModifiers.FIT_PAGE:
        this.performCustomZoom(
          {
            pageIndex,
            xInPage: 0,
            yInPage: 0
          },
          {
            newZoomLevel: this.getFitPageZoomLevel(pageIndex),
            newPageViewModifier: pageViewModifiers.FIT_PAGE
          }
        );
        break;
      case pageViewModifiers.FIXED_WIDTH:
        if (lastViewState) {
          this.performCustomZoom(lastViewState, {
            newZoomLevel: this.getFixedWidthZoomLevel(pageIndex),
            newPageViewModifier: pageViewModifiers.FIXED_WIDTH
          });
        } else {
          this.performCustomZoom(
            {
              pageIndex,
              xInPage: 0,
              yInPage: 0
            },
            {
              newZoomLevel: this.getFixedWidthZoomLevel(pageIndex),
              newPageViewModifier: pageViewModifiers.FIXED_WIDTH
            }
          );
        }
        break;
      case pageViewModifiers.FIT_HEIGHT:
        this.performCustomZoom(
          {
            pageIndex,
            xInPage: 0,
            yInPage: 0
          },
          {
            newZoomLevel: this.getFitHeightZoomLevel(),
            newPageViewModifier: pageViewModifiers.FIT_HEIGHT
          }
        );
        break;
      case pageViewModifiers.NO_MOD:
        this.pageViewModifier = pageViewModifiers.NO_MOD;
        break;
      default:
        break;
    }
  }

  @action
  setFixedWidthMargin(left, right) {
    const newMargins = { left, right };

    /**
     * Set the provided fixed width margins only one time. Used to restore
     * back to fixed width mode when toggling between normal and fixed width
     * mode.
     */
    if (!this.providedFixedWidthMargin) {
      this.providedFixedWidthMargin = newMargins;
    }
    this.setFixedMargins(newMargins);
    overlayEvents.trigger("fixedWidthModeToggle", true);
  }

  _shouldChangeToNoMode(changeToNoMode) {
    if (isFitOneFullPageMode(this.stores) && this.pageViewModifier === pageViewModifiers.FIT_PAGE) {
      return false;
    }
    return changeToNoMode;
  }

  @action
  removeFixedWidthMode(changeToNoMode = true) {
    if (this._shouldChangeToNoMode(changeToNoMode)) {
      this.pageViewModifier = pageViewModifiers.NO_MOD;
    }
    if (!isFitOneFullPageMode(this.stores)) {
      this.setFixedMargins({
        left: "auto",
        right: "auto"
      });
    }
    overlayEvents.trigger("fixedWidthModeToggle", false);
  }

  setProvidedFixedWidthMargin(left, right) {
    this.providedFixedWidthMargin = {
      left,
      right
    };
  }

  @action
  setPinchZoomCenterParameters(pinchZoomCenterParameters) {
    this.pinchZoomStore.setCenterParameters(pinchZoomCenterParameters);
  }

  @action
  setPinchZoomInitialZoom(initialZoom) {
    this.pinchZoomStore.setInitialZoom(initialZoom);
  }

  @action
  doPinchZoom({ newZoomLevel }, isPinchZoomInProgress = true) {
    if (!isPinchZoomInProgress) {
      // pinch zoom terminates here
      const initialZoomLevel = getPinchZoomInitialZoom(this.stores);
      if (newZoomLevel > initialZoomLevel) {
        // zoom in
        trackZoomIn();
      } else if (newZoomLevel < initialZoomLevel) {
        // zoom out
        trackZoomOut();
      }
    }
    let haveZoomlevelsChanged = false;
    let pagesInViewObj = pagesInView(this.stores);

    let initialPageZoomLevel = this.pageZoomLevel;
    this.setNewZoomLevelObservables(newZoomLevel, pageViewModifiers.NO_MOD, pagesInViewObj.start);
    if (initialPageZoomLevel !== this.pageZoomLevel) {
      haveZoomlevelsChanged = true;
    }
    this.pageViewModifier = pageViewModifiers.NO_MOD;
    if (!isPinchZoomInProgress && isClassicPDF(this.stores)) {
      this.stores.documentStore.getPageRenditionInfo &&
        this.stores.documentStore.getPageRenditionInfo.numZoomOperations++;
      this._reloadPages(pagesInViewObj);
    } else {
      this.stores.pagedViewState.scrollPagesView(
        getPinchZoomScrollLeft(this.stores),
        getPinchZoomScrollTop(this.stores),
        false,
        false
      );
    }
    this.stores.documentStore.setShouldRecomputePageSizes(haveZoomlevelsChanged);
    contextMenuManager.clearMenuItems(this.stores);
  }

  @action
  doDoubleTapZoom(event) {
    let centerPageIndex = getCurrentPageIndex(this.stores);
    let pageRect = getPageHandle(this.stores, centerPageIndex).getBoundingClientRect();
    let w = pageRect.right - pageRect.left;
    let h = pageRect.bottom - pageRect.top;
    let centerXRatio = (event.center.x - pageRect.left) / w;
    let centerYRatio = (event.center.y - pageRect.top) / h;
    this.setPinchZoomCenterParameters({
      centerX: event.center.x,
      centerY: event.center.y,
      centerPageIndex,
      centerXRatio,
      centerYRatio
    });
    const initialZoomLevel = getCurrentPageZoomLevel(this.stores);
    this.setPinchZoomInitialZoom(initialZoomLevel);
    let minZoom = this.minZoom / getDevicePixelRatio();
    let maxZoom = this.maxZoom / getDevicePixelRatio();
    let finalZoomLevel, isZoomingIn;
    if (initialZoomLevel === this.getFitWidthZoomLevel()) {
      finalZoomLevel = maxZoom;
      isZoomingIn = true;
    } else {
      finalZoomLevel = this.getFitWidthZoomLevel();
      isZoomingIn = false;
    }

    var interval = setInterval(() => {
      let newZoomLevel = this.getDoubleTapNewZoomLevel(isZoomingIn);
      if (ZoomUtils.getTruncatedZoomLevel(minZoom, maxZoom, newZoomLevel) === finalZoomLevel) {
        // Resetting PinchZoomStore
        this.setPinchZoomCenterParameters({
          centerX: 0,
          centerY: 0,
          centerPageIndex: 0,
          centerXRatio: 0,
          centerYRatio: 0
        });
        if (this.getFitWidthZoomLevel() === finalZoomLevel) {
          this.setPageViewModifier({ newPageViewModifier: pageViewModifiers.FIT_WIDTH });
        }
        clearInterval(interval);
      } else this.doPinchZoom({ newZoomLevel }, true);
    }, 1);
  }

  @action
  handleMobileResizing() {
    if (isCurrentDeviceMobile() && getMaxPageWidth(this.stores) > 0) {
      const prevMinZoom = roundOffTo2Decimal(this.minZoom / getDevicePixelRatio());
      const prevMaxZoom = roundOffTo2Decimal(this.maxZoom / getDevicePixelRatio());
      this.setZoomLimits({
        minZoom: getMinZoomLevelForMobile(this.stores, prevMinZoom, prevMaxZoom)
      });
      if (prevMinZoom === getCurrentPageZoomLevel(this.stores)) {
        const pageViewModifier = getPageViewModifier(this.stores);
        if (pageViewModifier !== pageViewModifiers.NO_MOD) {
          this.setPageViewModifier({ newPageViewModifier: pageViewModifier });
        }
      }
    }
  }

  disableInitialZoomLevelChange() {
    this.allowInitialZoomlevelChange = false;
  }

  getIsCommentsPanelVisible() {
    return this.isCommentsPanelVisible;
  }

  getCurrPageZoomLevel(pageIndex) {
    this.zoomLevelPageIndex = pageIndex;
    return this.getNewPageZoomLevel;
  }

  @computed
  get getNewPageZoomLevel() {
    let minZoom = this.minZoom / getDevicePixelRatio();
    let maxZoom = this.maxZoom / getDevicePixelRatio();
    let pageZoomLevel = ZoomUtils.getTruncatedZoomLevel(minZoom, maxZoom, this.pageZoomLevel),
      pageViewModifier = this.pageViewModifier,
      pageIndex = this.zoomLevelPageIndex;
    if (pageViewModifier === pageViewModifiers.FIT_PAGE) {
      pageZoomLevel = this.getFitPageZoomLevel(pageIndex);
      // minZoom = pagedata.minZoom / getDevicePixelRatio();
      // maxZoom = pagedata.maxZoom / getDevicePixelRatio();
    } else if (pageViewModifier === pageViewModifiers.FIT_HEIGHT) {
      pageZoomLevel = this.getFitHeightZoomLevel();
    } else if (pageViewModifier === pageViewModifiers.FIT_WIDTH) {
      pageZoomLevel = this.getFitWidthZoomLevel();
    } else if (pageViewModifier === pageViewModifiers.FIXED_WIDTH) {
      pageZoomLevel = this.getFixedWidthZoomLevel(pageIndex);
    }
    if (isFullScreenOrFitOnePageMode(this.stores)) {
      const fitPageZoom = this.getFitPageZoomLevel(pageIndex);
      maxZoom = Math.max(maxZoom, fitPageZoom);
    }
    if (isSinglePageMode(this.stores)) {
      minZoom = this.getFitPageZoomLevel(pageIndex);
      if (pageIndex !== getCurrentPageIndex(this.stores)) {
        pageZoomLevel = minZoom;
      }
    }
    pageZoomLevel = ZoomUtils.getTruncatedZoomLevel(minZoom, maxZoom, pageZoomLevel);
    return pageZoomLevel;
  }

  getFitPageZoomLevel(pageIndex) {
    const rowIndex = getRowIndex(this.stores, pageIndex);
    let rowOrigHeight = getRowOrigHeight(this.stores, rowIndex);
    let rowOrigWidth = getRowOrigWidth(this.stores, rowIndex);

    if (!rowOrigHeight || !rowOrigWidth) {
      return constants.DEFAULT_ZOOM_LEVEL;
    }
    const viewPortWidth =
      isTwoColumnViewMode(this.stores) ||
      isFitOneFullPageMode(this.stores) ||
      getPageViewModifier(this.stores) === pageViewModifiers.FIT_PAGE
        ? getViewPortWidthWithoutPagesViewScrollbar(this.stores) - this.getMarginsDiff()
        : getViewPortWidthWithoutPagesViewScrollbar(this.stores);
    const zoomReqdForWidth = viewPortWidth / rowOrigWidth;

    let viewPortHeight = getViewPortHeight(this.stores);
    viewPortHeight = getTotalPages(this.stores) === 1 ? viewPortHeight - PAGE_BOTTOM_MARGIN : viewPortHeight;

    const zoomReqdForHeight = viewPortHeight / rowOrigHeight;
    return Math.min(zoomReqdForHeight, zoomReqdForWidth);
  }

  getFitHeightZoomLevel() {
    const maxPageHeight = getMaxPageHeight(this.stores);
    if (!maxPageHeight) {
      return constants.DEFAULT_ZOOM_LEVEL;
    }
    return getViewPortHeightWithoutPagesViewScrollbar(this.stores) / maxPageHeight;
  }

  getMarginsDiff() {
    let diff =
      this.fixedMargins.left !== "auto" && this.fixedMargins.right !== "auto"
        ? this.fixedMargins.left + this.fixedMargins.right
        : 0;
    if (isTwoColumnViewMode(this.stores)) {
      diff += constants.TWO_COLUMN_MARGIN + constants.PAGES_VIEW_MARGIN_LEFT + constants.PAGES_VIEW_MARGIN_RIGHT;
    }
    return diff;
  }

  getFitWidthZoomLevel(widthGetter = getViewPortWidthWithoutPagesViewScrollbar) {
    const maxPageWidth = getMaxPageWidth(this.stores);
    if (!maxPageWidth) {
      return constants.DEFAULT_ZOOM_LEVEL;
    }
    let availableViewWidth = widthGetter(this.stores) - this.getMarginsDiff();
    if (isTwoColumnViewMode(this.stores)) {
      availableViewWidth = Math.floor(availableViewWidth / 2);
    }
    return availableViewWidth / maxPageWidth;
  }

  getFixedWidthZoomLevel(pageIndex) {
    const rowIndex = getRowIndex(this.stores, pageIndex);
    let rowOrigWidth = getRowOrigWidth(this.stores, rowIndex);
    if (!rowOrigWidth) {
      return constants.DEFAULT_ZOOM_LEVEL;
    }

    const diff = this.getMarginsDiff();
    const zoomReqdForWidth = (getViewPortWidthWithoutPagesViewScrollbar(this.stores) - diff) / rowOrigWidth;
    return zoomReqdForWidth;
  }

  getDoubleTapNewZoomLevel(isZoomingIn) {
    let currentPageZoomLevel = getCurrentPageZoomLevel(this.stores);
    if (isZoomingIn) currentPageZoomLevel += 0.05;
    else currentPageZoomLevel -= 0.05;
    return roundOffTo2Decimal(currentPageZoomLevel);
  }
}

export { Zoom };
